import {
  AllowedStripeBusinessType,
  type Alpha2CountryCodes,
} from '#app/__generated__/api.js';
import { type PayoutAccountEligibility } from '#test/__generated__/api.js';

export type InitialState = {
  canEditCountry: State['canEditCountry'];
  initialCountryCode: State['initialCountryCode'];
  payoutAccountEligibility: State['payoutAccountEligibility'];
  stripeRequiresOnboardingAction: State['stripeRequiresOnboardingAction'];
  verifiedIdentity: State['verifiedIdentity'];
};

export type PayoutType =
  | 'PAYOUT_Airwallex_Local_Bank'
  | 'PAYOUT_Airwallex_US_Bank_Only'
  | 'PAYOUT_Crypto'
  | 'PAYOUT_Payoneer'
  | 'PAYOUT_Paypal'
  | 'PAYOUT_Stripe_Local_Bank'
  | 'PAYOUT_Stripe_Local_Debit_Card';

export type State = {
  canEditCountry: boolean;
  initialCountryCode: Alpha2CountryCodes | null;
  payoutAccountEligibility: PayoutAccountEligibility;
  payoutTypesEnabled: PayoutTypesEnabled;
  paypalAuthenticationCode?: null | string;
  selectedBusinessType: StripeBusinessType;
  selectedPayoutType: null | PayoutType;
  stripeRequiresOnboardingAction: boolean;
  verifiedIdentity: null | {
    location: null | string;
    locationMatchesUserAccountLocation: boolean;
  };
  view: null | View;
};

type PayoutTypesEnabled = { [key in PayoutType]: boolean };

type StripeBusinessType = Exclude<
  AllowedStripeBusinessType,
  '%future added value'
>;

type View =
  | 'VIEW_Airwallex_Beneficiary_Form'
  | 'VIEW_Bank_Account_Form'
  | 'VIEW_Country_Not_Supported'
  | 'VIEW_Crypto_Disclaimer'
  | 'VIEW_Crypto_Education'
  | 'VIEW_Crypto_Form'
  | 'VIEW_Debit_Card_Form'
  | 'VIEW_Payoneer_Return'
  | 'VIEW_Payout_Setup_Successful'
  | 'VIEW_Paypal_Connect_Form'
  | 'VIEW_Paypal_Return'
  | 'VIEW_Persona'
  | 'VIEW_Persona_Failure'
  | 'VIEW_Persona_Note'
  | 'VIEW_Persona_Update_Location'
  | 'VIEW_Sanctioned_Country'
  | 'VIEW_Select_Country'
  | 'VIEW_Select_Payout_Method'
  | 'VIEW_Stripe_More_Information'
  | 'VIEW_Stripe_Note'
  | 'VIEW_Stripe_Return';

export const defaultState: State = {
  canEditCountry: true,
  initialCountryCode: null,
  payoutAccountEligibility: {
    airwallexPayouts: {
      airwallexBankAccountSupport: 'LOCAL_BANK_VIA_LOCAL_RAIL',
      eligibilityStatus: 'ELIGIBLE',
    },
    payoneerPayouts: {
      eligibilityStatus: 'ELIGIBLE',
    },
    paypalPayouts: {
      eligibilityStatus: 'ELIGIBLE',
    },
    stripeBankAccountPayouts: {
      eligibilityStatus: 'ELIGIBLE',
      flow: {
        __typename: 'StripeBankAccountPayoutAccountEligibilityFlowContraInApp',
        prerequisite: 'PERSONA_VERIFICATION',
      },
    },
    stripeDebitCardPayouts: {
      eligibilityStatus: 'ELIGIBLE',
      prerequisite: 'BANK_ACCOUNT',
    },
    usdcPayouts: {
      eligibilityStatus: 'ELIGIBLE',
      prerequisite: 'PERSONA_VERIFICATION',
    },
  },
  payoutTypesEnabled: {
    PAYOUT_Airwallex_Local_Bank: false,
    PAYOUT_Airwallex_US_Bank_Only: false,
    PAYOUT_Crypto: false,
    PAYOUT_Payoneer: false,
    PAYOUT_Paypal: false,
    PAYOUT_Stripe_Local_Bank: false,
    PAYOUT_Stripe_Local_Debit_Card: false,
  },
  paypalAuthenticationCode: null,
  selectedBusinessType: 'INDIVIDUAL',
  selectedPayoutType: null,
  stripeRequiresOnboardingAction: true,
  verifiedIdentity: null,
  view: null,
};
